import { Button, DialogActions } from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  ActBlueImportIntegration,
  CreateActBlueImportIntegrationInput,
  UpdateActBlueImportIntegrationInput,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  CREATE_ACT_BLUE_IMPORT_INTEGRATION,
  UPDATE_ACT_BLUE_IMPORT_INTEGRATION,
} from "~frontend/graphql/integration";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const creationValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  clientUuid: Yup.string().required("Required"),
  clientSecret: Yup.string().required("Required"),
});

const updateValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  clientUuid: Yup.string().required("Required"),
  clientSecret: Yup.string(),
});

function creationIntegrationValues(
  subaccountId: string,
  values: any
): CreateActBlueImportIntegrationInput {
  const { name, clientUuid, clientSecret } = values;
  return {
    subaccountId,
    name,
    clientUuid,
    clientSecret,
  };
}

function updateIntegrationValues(
  integrationId: string,
  values: any
): UpdateActBlueImportIntegrationInput {
  const { name, clientUuid } = values;

  return {
    integrationId,
    name,
    clientUuid,
    ...(values.clientSecret ? { clientSecret: values.clientSecret } : {}),
  };
}

export function ActBlueImportIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: ActBlueImportIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: () => void;
}): ReactElement {
  const editing = !!integration;

  const [createActBlueImportIntegration] = useMutation(
    CREATE_ACT_BLUE_IMPORT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  const [updateActBlueImportIntegration] = useMutation(
    UPDATE_ACT_BLUE_IMPORT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  async function createIntegration(
    values: CreateActBlueImportIntegrationInput
  ) {
    const { errors } = await createActBlueImportIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }
    if (afterCreate) {
      afterCreate();
    }

    onClose();
  }

  async function updateIntegration(
    values: UpdateActBlueImportIntegrationInput
  ) {
    const { errors } = await updateActBlueImportIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }

    onClose();
  }

  const getErrorMessage = useErrorMessageFormatter();
  const label = editing
    ? "Client Secret (leave blank to keep current)"
    : "Client Secret";

  const initialValues = {
    name: integration?.name || "",
    clientUuid: integration?.settings?.clientUuid || "",
    clientSecret: "",
  };

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        ...initialValues,
      }}
      validationSchema={
        editing ? updateValidationSchema : creationValidationSchema
      }
      onSubmit={async (values, formik) => {
        try {
          if (editing) {
            await updateIntegration(
              updateIntegrationValues(integration.id, values)
            );
          } else {
            await createIntegration(
              creationIntegrationValues(subaccountId, values)
            );
          }
          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      {({ isSubmitting }) => {
        return (
          <Form>
            <FormikErrorBanner />
            <FormikTextField
              name="name"
              label="Name Your Integration"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              name="clientUuid"
              label="Client UUID"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              label={label}
              name="clientSecret"
              type="password"
              disabled={isSubmitting}
              fullWidth
            />
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={() => onClose()}
                color="secondary"
              >
                Cancel
              </Button>
              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText={editing ? "Save" : "Create"}
                loadingText={editing ? "Saving..." : "Creating..."}
              />
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}
